import { ArrowLeft, Search, X } from 'lucide-react';
import { cn } from '@sshop/ui';
import { readSearchHistory } from '../searchHistory';
import { headerSearchInputStyles as s } from './HeaderSearchInput.styles';
import type { HeaderSearchInputProps } from './HeaderSearchInput.types';

export function HeaderSearchInput({ search }: HeaderSearchInputProps) {
  const {
    t,
    navigate,
    value,
    setValue,
    setTerm,
    open,
    setOpen,
    mobile,
    activeIndex,
    setActiveIndex,
    history,
    setHistory,
    inputRef,
    query,
    optionUrls,
    resultSection,
    collectionsUrl,
    discovery,
    optionCount,
    closeSearch,
    navigateToSearch,
    finishNavigation,
    onFocusChange,
    restoreFocus,
  } = search;
  return (
    <form
      className={cn(s.form, !open && s.formCompactClosed)}
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        if (discovery && activeIndex >= 0 && history[activeIndex])
          navigateToSearch(history[activeIndex]!);
        else if (!discovery && activeIndex >= 0 && optionUrls[activeIndex]) {
          const url = optionUrls[activeIndex]!;
          finishNavigation();
          navigate(url);
        } else if (!discovery && mobile && resultSection === 'collections' && collectionsUrl) {
          finishNavigation();
          navigate(collectionsUrl);
        } else navigateToSearch(query);
      }}
    >
      {open && mobile && (
        <button
          type="button"
          className={s.iconButton}
          aria-label={t('search.close')}
          onClick={() => {
            closeSearch();
            restoreFocus();
          }}
        >
          <ArrowLeft className={s.iconGlyph} aria-hidden />
        </button>
      )}
      <div className={s.inputWrap}>
        <span className={s.inputIcon} aria-hidden>
          <Search className={s.iconGlyph} />
        </span>
        <input
          ref={inputRef}
          type="search"
          inputMode="search"
          autoComplete="off"
          className={s.input}
          value={value}
          placeholder={t('search.placeholder')}
          aria-label={t('nav.search')}
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={
            open && optionCount > 0
              ? discovery
                ? 'header-search-options'
                : mobile
                  ? `header-search-${resultSection}-options`
                  : 'header-search-products-options header-search-collections-options'
              : undefined
          }
          aria-activedescendant={
            open && activeIndex >= 0 && activeIndex < optionCount
              ? `header-search-${discovery ? 'history' : 'option'}-${activeIndex}`
              : undefined
          }
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => {
            if (!open) setHistory(readSearchHistory());
            setOpen(true);
            onFocusChange?.(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              if (optionCount > 0)
                setActiveIndex((current) =>
                  event.key === 'ArrowDown'
                    ? (current + 1) % optionCount
                    : current <= 0
                      ? optionCount - 1
                      : current - 1,
                );
            }
          }}
        />
        {value && (
          <button
            type="button"
            className={s.clearInput}
            aria-label={t('search.clearQuery')}
            onClick={() => {
              setValue('');
              setTerm('');
              setActiveIndex(-1);
              inputRef.current?.focus();
            }}
          >
            <X className={s.iconGlyph} aria-hidden />
          </button>
        )}
      </div>
    </form>
  );
}
